/**
 * JSON-LD building blocks with no dependency, shared by every brand's structured data. Pages import these
 * instead of a brand module (modules/medleveSeo/jsonLd/graph re-exports them), so they don't pull its registry.
 */

export type JsonLdValue = string | number | boolean | null | undefined | JsonLdValue[] | { [key: string]: JsonLdValue }

export interface JsonLdNode {
  '@type': string | string[]
  '@id': string
  [key: string]: JsonLdValue
}

export interface JsonLdGraph {
  '@context': 'https://schema.org'
  '@graph': JsonLdNode[]
}

/** Drops `undefined`, `null`, empty strings and empty arrays so unknown values are omitted, never faked. */
export const compact = <T extends { [key: string]: JsonLdValue }>(node: T): T =>
  Object.keys(node).reduce((acc, key) => {
    const value = node[key]
    const isEmpty = value === undefined || value === null || value === '' || (Array.isArray(value) && !value.length)
    return isEmpty ? acc : { ...acc, [key]: value }
  }, {} as T)

/** Serializes for an inline `<script>`: escapes characters that could close the tag or break parsers. */
export const serializeJsonLd = (data: unknown): string =>
  JSON.stringify(data)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029')
